import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, Avatar, Button, Tabs, Tab,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  IconButton, Alert, Switch, FormControlLabel, Divider, MenuItem, Table,
  TableBody, TableCell, TableHead, TableRow
} from '@mui/material';
import { ArrowBack, Edit, Add, Delete, Close, CalendarMonth, EventBusy } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import toast from 'react-hot-toast';

const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
const dayLabels: Record<string, string> = { monday:'Mon',tuesday:'Tue',wednesday:'Wed',thursday:'Thu',friday:'Fri',saturday:'Sat',sunday:'Sun' };

const TabPanel = ({ value, index, children }: any) => (
  <Box hidden={value !== index} sx={{ pt: 2 }}>{value === index && children}</Box>
);

const DoctorProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [schedule, setSchedule] = useState<any[]>([]);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [leaveDialog, setLeaveDialog] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ date: '', reason: '', type: 'full_day' });
  const [stats, setStats] = useState<any>({});

  const load = async () => {
    try {
      const [drRes, statsRes] = await Promise.all([
        api.get(`/doctors/${id}`),
        api.get(`/doctors/${id}/stats`)
      ]);
      setDoctor(drRes.data.data);
      const ws = drRes.data.data.weeklySchedule || [];
      // Ensure all days present
      setSchedule(DAYS.map(day => ws.find((s: any) => s.day === day) || { day, isWorking: day !== 'sunday', startTime: '09:00', endTime: '17:00', slotDuration: 30, maxPatientsPerDay: 20 }));
      setStats(statsRes.data.data || {});
    } catch { toast.error('Failed to load doctor'); navigate('/doctors'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const saveSchedule = async () => {
    setSavingSchedule(true);
    try {
      await api.put(`/doctors/${id}/schedule`, { weeklySchedule: schedule });
      toast.success('Schedule saved');
      load();
    } catch { toast.error('Failed to save schedule'); }
    finally { setSavingSchedule(false); }
  };

  const addLeave = async () => {
    try {
      await api.post(`/doctors/${id}/leaves`, leaveForm);
      toast.success('Leave added');
      setLeaveDialog(false);
      load();
    } catch { toast.error('Failed to add leave'); }
  };

  const removeLeave = async (leaveId: string) => {
    try {
      await api.delete(`/doctors/${id}/leaves/${leaveId}`);
      toast.success('Leave removed');
      load();
    } catch { toast.error('Failed to remove leave'); }
  };

  const updateDay = (day: string, field: string, value: any) =>
    setSchedule(s => s.map(d => d.day === day ? { ...d, [field]: value } : d));

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><CircularProgress /></Box>;
  if (!doctor) return null;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/doctors')} sx={{ bgcolor: 'white', border: '1px solid #e2e8f0', borderRadius: 2 }}>
          <ArrowBack fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>Dr. {doctor.user?.name}</Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>{doctor.specialization}</Typography>
        </Box>
        <Button variant="contained" onClick={() => navigate(`/appointments?doctor=${id}`)}
          sx={{ borderRadius: 2, bgcolor: '#1e40af', textTransform: 'none', fontWeight: 600 }}>
          Book Appointment
        </Button>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {[
          { label: "Today's Appointments", value: stats.todayAppointments ?? 0, color: '#3b82f6', bg: '#eff6ff' },
          { label: 'Total Appointments', value: stats.totalAppointments ?? 0, color: '#10b981', bg: '#ecfdf5' },
          { label: 'Completed', value: stats.completedAppointments ?? 0, color: '#8b5cf6', bg: '#f5f3ff' },
          { label: 'Consultation Fee', value: `₹${doctor.consultationFee || 0}`, color: '#f59e0b', bg: '#fffbeb' },
        ].map(({ label, value, color, bg }) => (
          <Grid item xs={6} sm={3} key={label}>
            <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3 }}>
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color }}>{value}</Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>{label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3 }}>
        {/* Profile Header */}
        <CardContent sx={{ p: 3, borderBottom: '1px solid #f1f5f9' }}>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
            <Avatar sx={{ width: 80, height: 80, bgcolor: '#dbeafe', color: '#1d4ed8', fontSize: 28, fontWeight: 800 }}>
              {doctor.user?.name?.charAt(0)}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                {doctor.qualifications?.map((q: any) => (
                  <Chip key={q._id} label={q.degree} size="small" sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 600 }} />
                ))}
                {doctor.department && <Chip label={doctor.department.name} size="small" sx={{ bgcolor: '#dcfce7', color: '#15803d' }} />}
                <Chip label={doctor.acceptingNewPatients ? 'Accepting Patients' : 'Not Accepting'} size="small"
                  sx={{ bgcolor: doctor.acceptingNewPatients ? '#dcfce7' : '#fee2e2', color: doctor.acceptingNewPatients ? '#15803d' : '#b91c1c' }} />
              </Box>
              <Grid container spacing={2}>
                {[
                  { label: 'Email', value: doctor.user?.email },
                  { label: 'Phone', value: doctor.user?.phone },
                  { label: 'Reg. No.', value: doctor.registrationNumber },
                  { label: 'Experience', value: doctor.totalExperienceYears ? `${doctor.totalExperienceYears} years` : null },
                  { label: 'Languages', value: doctor.languages?.join(', ') },
                ].filter(x => x.value).map(({ label, value }) => (
                  <Grid item xs={6} sm={4} md={3} key={label}>
                    <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>{label}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{value}</Typography>
                  </Grid>
                ))}
              </Grid>
              {doctor.bio && <Typography variant="body2" sx={{ color: '#64748b', mt: 1 }}>{doctor.bio}</Typography>}
            </Box>
          </Box>
        </CardContent>

        <Box sx={{ borderBottom: '1px solid #f1f5f9', px: 2 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}
            sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 13 }, '& .Mui-selected': { color: '#1e40af' }, '& .MuiTabs-indicator': { bgcolor: '#1e40af' } }}>
            <Tab label="Qualifications & Experience" />
            <Tab label="Fees" />
            <Tab label="Weekly Schedule" />
            <Tab label="Leaves" />
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>

          {/* TAB 0: Qualifications & Experience */}
          <TabPanel value={tab} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Qualifications</Typography>
                {doctor.qualifications?.length === 0 && <Typography sx={{ color: '#94a3b8' }}>No qualifications added</Typography>}
                {doctor.qualifications?.map((q: any, i: number) => (
                  <Box key={i} sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, mb: 1, border: '1px solid #e2e8f0' }}>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#1e40af' }}>{q.degree}</Typography>
                    <Typography variant="body2" sx={{ color: '#374151' }}>{q.institution}</Typography>
                    {q.year && <Typography variant="caption" sx={{ color: '#94a3b8' }}>Class of {q.year}</Typography>}
                  </Box>
                ))}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Experience</Typography>
                {doctor.experience?.length === 0 && <Typography sx={{ color: '#94a3b8' }}>No experience added</Typography>}
                {doctor.experience?.map((e: any, i: number) => (
                  <Box key={i} sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, mb: 1, border: '1px solid #e2e8f0' }}>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>{e.position}</Typography>
                    <Typography variant="body2" sx={{ color: '#374151' }}>{e.hospital}</Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      {e.from ? new Date(e.from).getFullYear() : '?'} — {e.isCurrent ? 'Present' : e.to ? new Date(e.to).getFullYear() : '?'}
                    </Typography>
                  </Box>
                ))}
              </Grid>
            </Grid>
          </TabPanel>

          {/* TAB 1: Fees */}
          <TabPanel value={tab} index={1}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Consultation Fees</Typography>
            <Grid container spacing={2}>
              {[
                { label: 'Consultation', value: doctor.consultationFee, color: '#3b82f6' },
                { label: 'Follow-up', value: doctor.followUpFee, color: '#10b981' },
                { label: 'Emergency', value: doctor.emergencyFee, color: '#ef4444' },
                { label: 'Online Consultation', value: doctor.onlineConsultationFee, color: '#8b5cf6' },
              ].map(({ label, value, color }) => (
                <Grid item xs={6} sm={3} key={label}>
                  <Card elevation={0} sx={{ border: `2px solid ${color}20`, borderRadius: 3, p: 2, textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, color }}>₹{value || 0}</Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>{label}</Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </TabPanel>

          {/* TAB 2: Schedule */}
          <TabPanel value={tab} index={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Weekly Schedule</Typography>
              <Button variant="contained" onClick={saveSchedule} disabled={savingSchedule}
                sx={{ borderRadius: 2, textTransform: 'none', bgcolor: '#1e40af' }}>
                {savingSchedule ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Save Schedule'}
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {schedule.map(day => (
                <Card key={day.day} elevation={0} sx={{ border: `1px solid ${day.isWorking ? '#e2e8f0' : '#f1f5f9'}`, borderRadius: 2, bgcolor: day.isWorking ? 'white' : '#fafafa' }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <FormControlLabel
                        control={<Switch checked={day.isWorking} onChange={e => updateDay(day.day, 'isWorking', e.target.checked)} size="small" />}
                        label={<Typography sx={{ fontWeight: 700, textTransform: 'capitalize', minWidth: 80, color: day.isWorking ? '#0f172a' : '#94a3b8' }}>{dayLabels[day.day]}</Typography>}
                        sx={{ m: 0, gap: 1 }}
                      />
                      {day.isWorking && (
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', whiteSpace: 'nowrap' }}>Start</Typography>
                            <TextField type="time" size="small" value={day.startTime || '09:00'} onChange={e => updateDay(day.day, 'startTime', e.target.value)}
                              sx={{ width: 120, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', whiteSpace: 'nowrap' }}>End</Typography>
                            <TextField type="time" size="small" value={day.endTime || '17:00'} onChange={e => updateDay(day.day, 'endTime', e.target.value)}
                              sx={{ width: 120, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', whiteSpace: 'nowrap' }}>Slot</Typography>
                            <TextField select size="small" value={day.slotDuration || 30} onChange={e => updateDay(day.day, 'slotDuration', Number(e.target.value))}
                              sx={{ width: 100, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                              {[15, 20, 30, 45, 60].map(m => <MenuItem key={m} value={m}>{m} min</MenuItem>)}
                            </TextField>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', whiteSpace: 'nowrap' }}>Max pts</Typography>
                            <TextField type="number" size="small" value={day.maxPatientsPerDay || 20} onChange={e => updateDay(day.day, 'maxPatientsPerDay', Number(e.target.value))}
                              sx={{ width: 80, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                          </Box>
                        </>
                      )}
                      {!day.isWorking && <Typography variant="caption" sx={{ color: '#94a3b8' }}>Not working</Typography>}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </TabPanel>

          {/* TAB 3: Leaves */}
          <TabPanel value={tab} index={3}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Leaves & Unavailability</Typography>
              <Button variant="contained" startIcon={<Add />} onClick={() => setLeaveDialog(true)}
                sx={{ borderRadius: 2, textTransform: 'none', bgcolor: '#1e40af' }}>
                Add Leave
              </Button>
            </Box>
            {doctor.leaves?.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <EventBusy sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                <Typography sx={{ color: '#94a3b8' }}>No leaves scheduled</Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {doctor.leaves?.sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())
                .map((leave: any) => (
                <Box key={leave._id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: '#fef2f2', borderRadius: 2, border: '1px solid #fecaca' }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {new Date(leave.date).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                      <Chip label={leave.type?.replace('_', ' ')} size="small" sx={{ textTransform: 'capitalize', bgcolor: '#fee2e2', color: '#b91c1c', fontSize: 10 }} />
                      {leave.reason && <Typography variant="caption" sx={{ color: '#64748b' }}>{leave.reason}</Typography>}
                    </Box>
                  </Box>
                  <IconButton size="small" onClick={() => removeLeave(leave._id)} sx={{ color: '#ef4444' }}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </TabPanel>
        </Box>
      </Card>

      {/* Leave Dialog */}
      <Dialog open={leaveDialog} onClose={() => setLeaveDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between' }}>
          Add Leave
          <IconButton size="small" onClick={() => setLeaveDialog(false)}><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Date *</Typography>
              <TextField fullWidth size="small" type="date" value={leaveForm.date} onChange={e => setLeaveForm(f => ({ ...f, date: e.target.value }))}
                InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Type</Typography>
              <TextField select fullWidth size="small" value={leaveForm.type} onChange={e => setLeaveForm(f => ({ ...f, type: e.target.value }))}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                <MenuItem value="full_day">Full Day</MenuItem>
                <MenuItem value="half_day">Half Day</MenuItem>
              </TextField>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Reason</Typography>
              <TextField fullWidth size="small" value={leaveForm.reason} onChange={e => setLeaveForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="e.g. Personal, Conference" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setLeaveDialog(false)} sx={{ textTransform: 'none', color: '#64748b' }}>Cancel</Button>
          <Button variant="contained" onClick={addLeave}
            sx={{ borderRadius: 2, textTransform: 'none', bgcolor: '#1e40af' }}>Add Leave</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DoctorProfile;
